/** The waivers guests sign, in either mode: one row each, named from the operator's
 *  enabled waivers (the stored list carries ids only); in edit mode Remove and "Add a
 *  waiver". Names and the picker's search come from the host's `waivers` source.
 *
 *  `details` (a host that knows every waiver of the operator — the operator tab's GET): the
 *  rows read in the order guests see them (`sequence`; the list's own order is not what
 *  guests get), each with its languages and audience tags, and a row guests do not get says
 *  why in words: a disabled waiver (amber) or an id that is not the operator's at all. */
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, IconButton, Typography } from '@mui/material';

import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { waiverActions } from '../links';

import { AddRow, ListFailure, ROW_SX } from './rows';
import type { OptionSource, PrecheckOption, PrecheckTenant } from './sources';

/** A stored waiver row: the id, and the keys it carries along (`tags`, a row `id`). */
export interface PrecheckWaiverRow {
  waiver_id: number;
  [k: string]: unknown;
}

export interface WaiversEdit {
  disabled: boolean;
  onChange: (next: PrecheckWaiverRow[], label: string) => void;
}

export interface WaiverListProps {
  /** The stored or drafted rows. */
  value: unknown;
  /** Null: view mode. */
  edit: WaiversEdit | null;
  /** The operator's enabled waivers. */
  waivers: OptionSource;
  tenant: PrecheckTenant | null;
  /** Every waiver of the operator by id (undefined: not the operator's). */
  details?: (waiverId: number) => WaiverDetails | undefined;
  /** Without `details`: what a row the enabled waivers lack says (default: the tour
   *  editor's — its save drops such a row). */
  missingNote?: string;
}

const MISSING_NOTE = 'not among the operator’s enabled waivers — a save drops it';

/** What the host knows of one of the operator's waivers. */
export interface WaiverDetails {
  name: string;
  enabled: boolean;
  /** The order guests see waivers in. */
  sequence: number;
  languages: readonly string[];
  /** Audience tags' names (none: every guest). */
  tags: readonly string[];
}

/** A waiver row's status in words, or null when guests get it. */
export function waiverRowIssue(d: WaiverDetails | undefined): { tone: 'warning' | 'error'; text: string } | null {
  if (!d) return { tone: 'error', text: 'Not one of this operator’s waivers — remove it to save' };
  if (!d.enabled) return { tone: 'warning', text: 'Disabled — guests don’t see it' };
  return null;
}

export function WaiverList({ value, edit, waivers, tenant, details, missingNote = MISSING_NOTE }: WaiverListProps) {
  const stored = Array.isArray(value) ? (value as PrecheckWaiverRow[]) : [];
  // Guests see waivers by the operator's sequence: so do the rows, when it is known.
  const rows = details
    ? [...stored].sort((a, b) => (details(a.waiver_id)?.sequence ?? Infinity) - (details(b.waiver_id)?.sequence ?? Infinity))
    : stored;
  const nameOf = (id: number) => (details ? details(id)?.name : undefined) ?? waivers.list?.find((o) => o.id === id)?.name;
  return (
    <Box sx={{ display: 'grid', width: '100%', minWidth: 0 }} data-precheck-waivers>
      {rows.length === 0 && (
        <Box sx={ROW_SX} data-precheck-row="none">
          <Typography variant="body2" color="text.secondary">
            No waivers.
          </Typography>
        </Box>
      )}
      {rows.map((w) => {
        const name = nameOf(w.waiver_id);
        const d = details?.(w.waiver_id);
        const issue = details ? waiverRowIssue(d) : null;
        return (
          <Box key={w.waiver_id} data-precheck-row={`waiver-${w.waiver_id}`} sx={ROW_SX}>
            <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <ValueActions actions={waiverActions(w.waiver_id)}>
                <Typography variant="body2" noWrap>
                  {name ?? `Waiver #${w.waiver_id}`}
                </Typography>
              </ValueActions>
              {!details && !name && waivers.list !== undefined && waivers.complete && (
                <Typography variant="caption" color="warning.dark" noWrap>
                  ({missingNote})
                </Typography>
              )}
              {d && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }} data-waiver-facts>
                  {d.languages.length > 0 ? d.languages.map((l) => l.toUpperCase()).join(', ') : 'no text'}
                  {' · '}
                  {d.tags.length > 0 ? `for ${d.tags.join(', ')}` : 'every guest'}
                </Typography>
              )}
              {issue && (
                <Typography
                  variant="caption"
                  color={issue.tone === 'error' ? 'error' : 'warning.dark'}
                  noWrap
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}
                  data-waiver-issue={issue.tone}
                >
                  {issue.tone === 'error' ? (
                    <ErrorOutlineIcon aria-hidden sx={{ fontSize: 14 }} />
                  ) : (
                    <WarningAmberIcon aria-hidden sx={{ fontSize: 14 }} />
                  )}
                  {issue.text}
                </Typography>
              )}
            </Box>
            <Box sx={{ width: 40, flex: 'none', display: 'flex', justifyContent: 'flex-end' }}>
              {edit && (
                <IconButton
                  size="small"
                  aria-label={`Remove ${name ?? `waiver #${w.waiver_id}`}`}
                  disabled={edit.disabled}
                  onClick={() => edit.onChange(stored.filter((x) => x.waiver_id !== w.waiver_id), `Remove ${name ?? 'the waiver'}`)}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
          </Box>
        );
      })}
      {waivers.failed && <ListFailure what="the operator’s waivers" onRetry={waivers.retry} />}
      <AddRow>
        {edit && (
          <Box sx={{ width: 300 }}>
            <RemoteAutocomplete<PrecheckOption>
              label="Add a waiver"
              size="small"
              fetchOnEmpty
              value={null}
              disabled={edit.disabled}
              getOptionLabel={(o) => o.name ?? `#${o.id}`}
              getOptionKey={(o) => o.id}
              getOptionMeta={(o) => ({ name: o.name ?? `#${o.id}`, pk: o.id, tenant: tenant?.code ?? null })}
              queryFn={async (query) => (await waivers.search(query)).filter((o) => !stored.some((w) => w.waiver_id === o.id))}
              onChange={(v) => {
                const o = v as PrecheckOption | null;
                if (o) edit.onChange([...stored, { waiver_id: o.id, tags: [] }], `Add the ${o.name ?? 'waiver'} waiver`);
              }}
            />
          </Box>
        )}
      </AddRow>
    </Box>
  );
}
